import { assertCaptureResourcesSafe, maskCaptureDocument, omitCaptureElement } from './capture-policy'
import { abortableImageTask, checkImageAbort, MAX_REPORT_IMAGE_PIXELS, normalizeReportImage } from './images'
import type { ImageTaskOptions } from './images'

/** Call only from an explicit user action. Caller must abort on unmount/session,
 * membership or view-as changes, and must review the returned image before upload.
 * Masking covers defined regions only; this is not a pixel-perfect browser shot.
 * No logging, diagnostics scraping, upload, global listeners or preview URLs.
 * The renderer's clone task is not interruptible: cancellation rejects promptly,
 * removes our container and drains late work, which may finish asynchronously. */
export async function captureReportViewport({ signal }: ImageTaskOptions = {}): Promise<Blob> {
  checkImageAbort(signal)
  const { default: render } = await abortableImageTask(import('html2canvas-pro'), signal)
  checkImageAbort(signal)
  // Validate after import; clone starts synchronously in this same JS turn.
  assertCaptureResourcesSafe(document)
  const width = window.innerWidth, height = window.innerHeight
  if (width <= 0 || height <= 0 || width * height > MAX_REPORT_IMAGE_PIXELS) throw new Error('Capture viewport exceeds 25 million pixels')
  const scrollX = window.scrollX, scrollY = window.scrollY
  const container = document.createElement('div')
  container.setAttribute('data-problem-report-overlay', '')
  document.body.append(container)
  let canvas: HTMLCanvasElement | undefined
  const release = (value: HTMLCanvasElement) => { value.width = 0; value.height = 0 }
  try {
    canvas = await abortableImageTask(render(document.documentElement, {
      signal, width, height, x: scrollX, y: scrollY, scrollX, scrollY,
      windowWidth: width, windowHeight: height, scale: 1,
      logging: false, enablePerformanceMonitoring: false,
      allowTaint: false, useCORS: false, proxy: undefined, imageTimeout: 3000,
      removeContainer: true, iframeContainer: container,
      ignoreElements: omitCaptureElement,
      onclone: clone => { checkImageAbort(signal); maskCaptureDocument(clone) },
    }), signal, release)
    checkImageAbort(signal)
    const blob = await abortableImageTask(new Promise<Blob>((resolve, reject) => {
      canvas!.toBlob(value => value ? resolve(value) : reject(new Error('Capture encoding failed')), 'image/png')
    }), signal)
    return await normalizeReportImage(blob, { signal })
  } finally {
    container.remove()
    if (canvas) release(canvas)
  }
}
